import { useState, useEffect } from 'react'
import { format, isToday, isYesterday } from 'date-fns'
import { nl } from 'date-fns/locale'
import { Bell, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { PageHeader } from '@/components/common'
import NotificationTimeline from '@/components/common/NotificationTimeline'
import { DeleteDialog } from '@/components/dialogs/DeleteDialog'
import { Button, Card } from '@/components/ui'
import { mockNotifications } from '@/mocks/notifications'

const formatDate = (date: Date) => {
    if (isToday(date)) {
        return 'Vandaag'
    }
    if (isYesterday(date)) {
        return 'Gisteren'
    }
    return format(date, 'EEEE d MMMM yyyy', { locale: nl })
}

const NotificationsPage = () => {
    const { t } = useTranslation()
    const [notifications, setNotifications] = useState(mockNotifications)
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false)
    const [notificationToDelete, setNotificationToDelete] = useState<number | null>(null)

    useEffect(() => {
        document.title = 'MijnPS | Notificaties'
    }, [])

    // Sort notifications by date
    const sortedNotifications = notifications
        .sort((a, b) => b.date.getTime() - a.date.getTime())

    const handleDeleteNotification = (id: number) => {
        setNotificationToDelete(id)
        setDeleteDialogOpen(true)
    }

    const handleDeleteAll = () => {
        setDeleteAllDialogOpen(true)
    }

    const confirmDeleteNotification = () => {
        if (notificationToDelete) {
            setNotifications(prev => prev.filter(notification => notification.id !== notificationToDelete))
            setDeleteDialogOpen(false)
            setNotificationToDelete(null)
        }
    }

    const confirmDeleteAll = () => {
        setNotifications([])
        setDeleteAllDialogOpen(false)
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <PageHeader
                    title={t("notifications.title")}
                    description={t("notifications.description")}
                />
                {notifications.length > 0 && (
                    <Button
                        variant="destructive"
                        size="sm"
                        onClick={handleDeleteAll}
                        className="flex items-center gap-2"
                    >
                        <Trash2 className="h-4 w-4" />
                        {t("notifications.deleteAll")}
                    </Button>
                )}
            </div>

            <div className="p-4">
                <Card className="max-w-4xl mx-auto px-6 py-10">
                    <NotificationTimeline
                        notifications={sortedNotifications}
                        onDelete={handleDeleteNotification}
                        formatDate={formatDate}
                    />
                </Card>
                {notifications.length === 0 && (
                    <div className="flex flex-col items-center justify-center min-h-[400px] text-muted-foreground">
                        <Bell className="h-14 w-14 mb-4" strokeWidth={1.5} />
                        <p>{t("notifications.noNotifications")}</p>
                    </div>
                )}
            </div>

            <DeleteDialog
                open={deleteDialogOpen}
                onOpenChange={setDeleteDialogOpen}
                title={t("notifications.deleteTitle")}
                description={t("notifications.deleteDescription")}
                onConfirm={confirmDeleteNotification}
            />

            <DeleteDialog
                open={deleteAllDialogOpen}
                onOpenChange={setDeleteAllDialogOpen}
                title={t("notifications.deleteAllTitle")}
                description={t("notifications.deleteAllDescription")}
                onConfirm={confirmDeleteAll}
            />
        </div>
    )
}

export default NotificationsPage